import React, { useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { toast } from 'sonner';
import { UserCheck, Send, Car, AlertTriangle, CheckCircle2, ArrowRight } from 'lucide-react';
import { getBackendUrl, getApiUrl } from '../utils/config';

const BACKEND_URL = getBackendUrl();
const API = getApiUrl();

const CarTransfer = ({ car, onTransferComplete, trigger }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1); // 1: Form, 2: Confirmation, 3: Success
  
  const [formData, setFormData] = useState({
    recipient_email: '',
    message: ''
  });

  const [transferResult, setTransferResult] = useState(null);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.recipient_email) {
      toast.error('Recipient email is required');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/cars/${car.id}/transfer`, formData);
      
      setTransferResult(response.data);
      setStep(3); // Show success step
      toast.success('Car transferred successfully!');
      
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Transfer failed';
      toast.error(errorMessage);
      console.error('Transfer error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setStep(1);
    setFormData({ recipient_email: '', message: '' });
    setTransferResult(null);
    if (step === 3 && onTransferComplete) {
      onTransferComplete(); // Refresh car list
    }
  };

  const proceedToConfirmation = () => {
    if (!formData.recipient_email) {
      toast.error('Please enter recipient email');
      return;
    }
    setStep(2);
  };

  const defaultTrigger = (
    <Button variant="outline" className="border-orange-200 text-orange-700 hover:bg-orange-50">
      <Send className="w-4 h-4 mr-2" />
      Transfer Car
    </Button>
  );

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || defaultTrigger}
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        {step === 1 && (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center">
                <Send className="w-5 h-5 mr-2 text-orange-600" />
                Transfer Car Ownership
              </DialogTitle>
              <DialogDescription>
                Transfer this car and all its service history to another user
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4">
              {/* Car Details */}
              <div className="p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center space-x-3">
                  <Car className="w-8 h-8 text-blue-600" />
                  <div>
                    <div className="font-medium">{car.year} {car.make} {car.model}</div>
                    {car.vin && (
                      <div className="text-sm text-gray-600">VIN: {car.vin}</div>
                    )}
                    <div className="text-xs text-gray-500">
                      Owner: {car.owner_name}
                    </div>
                  </div>
                </div>
              </div>

              <form onSubmit={(e) => { e.preventDefault(); proceedToConfirmation(); }} className="space-y-4">
                {/* Recipient Email */}
                <div className="space-y-2">
                  <Label htmlFor="recipient_email">Recipient's Email Address *</Label>
                  <Input
                    id="recipient_email"
                    type="email"
                    value={formData.recipient_email}
                    onChange={(e) => handleChange('recipient_email', e.target.value)}
                    placeholder="Enter the new owner's email"
                    required
                    data-testid="recipient-email-input"
                  />
                  <p className="text-xs text-gray-500">
                    The recipient must have an existing account in Track My Garage
                  </p>
                </div>

                {/* Optional Message */}
                <div className="space-y-2">
                  <Label htmlFor="message">Message to New Owner (Optional)</Label>
                  <Textarea
                    id="message"
                    value={formData.message}
                    onChange={(e) => handleChange('message', e.target.value)}
                    placeholder="e.g., 'Hi! I'm transferring my car records to you. The last service was done in March.'"
                    rows={3}
                    data-testid="transfer-message-input"
                  />
                </div>

                {/* Warning */}
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                  <div className="flex">
                    <AlertTriangle className="w-5 h-5 text-yellow-600 mt-0.5 mr-2 flex-shrink-0" />
                    <div className="text-sm">
                      <p className="font-medium text-yellow-800">Important:</p>
                      <ul className="mt-1 text-yellow-700 space-y-1">
                        <li>• This will transfer complete ownership of the car</li>
                        <li>• All service history will be included</li>
                        <li>• You will lose access to this car permanently</li>
                        <li>• This action cannot be undone</li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Next Button */}
                <div className="flex gap-3 pt-4">
                  <Button 
                    type="submit" 
                    disabled={!formData.recipient_email}
                    className="flex-1 bg-orange-600 hover:bg-orange-700"
                    data-testid="proceed-to-confirmation"
                  >
                    <ArrowRight className="w-4 h-4 mr-2" />
                    Review Transfer
                  </Button>
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={handleClose}
                    className="flex-1"
                    data-testid="cancel-transfer"
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </div>
          </>
        )}

        {step === 2 && (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center">
                <AlertTriangle className="w-5 h-5 mr-2 text-red-600" />
                Confirm Car Transfer
              </DialogTitle>
              <DialogDescription>
                Please review the transfer details carefully
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4">
              {/* Transfer Summary */}
              <div className="space-y-3">
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <div className="text-center">
                    <div className="font-medium text-red-800">You are about to transfer:</div>
                    <div className="text-lg font-bold text-red-900 mt-1">
                      {car.year} {car.make} {car.model}
                    </div>
                    {car.vin && (
                      <div className="text-sm text-red-700 mt-1">VIN: {car.vin}</div>
                    )}
                  </div>
                </div>

                <div className="text-center">
                  <ArrowRight className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                  <div className="text-sm text-gray-600">TO</div>
                </div>

                <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                  <div className="text-center">
                    <UserCheck className="w-8 h-8 text-green-600 mx-auto mb-2" />
                    <div className="font-medium text-green-800">New Owner:</div>
                    <div className="text-lg font-bold text-green-900">
                      {formData.recipient_email}
                    </div>
                  </div>
                </div>

                {formData.message && (
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="text-sm text-blue-800">
                      <div className="font-medium mb-1">Your message:</div>
                      <div className="italic">"{formData.message}"</div>
                    </div>
                  </div>
                )}
              </div>

              {/* Final Warning */}
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <div className="text-center text-sm text-red-800">
                  <div className="font-bold">⚠️ FINAL WARNING ⚠️</div>
                  <div className="mt-1">
                    This transfer is <span className="font-bold">PERMANENT</span> and cannot be undone.
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4">
                <Button 
                  onClick={handleSubmit}
                  disabled={loading}
                  className="flex-1 bg-red-600 hover:bg-red-700"
                  data-testid="confirm-transfer"
                >
                  {loading ? 'Transferring...' : 'Yes, Transfer Car'}
                </Button>
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setStep(1)}
                  disabled={loading}
                  className="flex-1"
                  data-testid="back-to-edit"
                >
                  Back to Edit
                </Button>
              </div>
            </div>
          </>
        )}

        {step === 3 && transferResult && (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center">
                <CheckCircle2 className="w-5 h-5 mr-2 text-green-600" />
                Transfer Complete!
              </DialogTitle>
              <DialogDescription>
                The car has been successfully transferred
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4">
              {/* Success Message */}
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-center">
                <CheckCircle2 className="w-12 h-12 text-green-600 mx-auto mb-3" />
                <div className="text-green-800">
                  <div className="font-bold text-lg">Transfer Successful!</div>
                  <div className="mt-2">
                    Your <span className="font-medium">
                      {transferResult.car_details?.year} {transferResult.car_details?.make} {transferResult.car_details?.model}
                    </span> has been transferred to <span className="font-medium">
                      {transferResult.transferred_to?.name || transferResult.transferred_to?.email}
                    </span>
                  </div>
                </div>
              </div>

              {/* Transfer Details */}
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Transferred to:</span>
                  <span className="font-medium">{transferResult.transferred_to?.email}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Service records included:</span>
                  <span className="font-medium">{transferResult.service_records_included}</span>
                </div>
                {transferResult.transfer_message && (
                  <div className="pt-2 border-t">
                    <span className="text-gray-600">Your message was delivered:</span>
                    <div className="mt-1 p-2 bg-gray-50 rounded text-xs italic">
                      "{transferResult.transfer_message}"
                    </div>
                  </div>
                )}
              </div>

              {/* Close Button */}
              <div className="pt-4">
                <Button 
                  onClick={handleClose}
                  className="w-full bg-green-600 hover:bg-green-700"
                  data-testid="close-success"
                >
                  Done
                </Button>
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CarTransfer;